import { useNavigate } from 'react-router-dom';
import OrdersService from '@api/Orders/OrderCtrl';
import { Order } from '@api/Orders/Order';
import { isOrderLineV2 } from '@api/Orders/OrderLine';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { currencyFormatter } from '@utils/Numbers';

/**
 * Lo que `/orders` resuelve MANDANDO el pedido al POS: retomarlo y cerrar una
 * mesa (consolidar sus cuentas y cobrarla allá).
 *
 * `posRoute` lo decide la pantalla según el perfil seleccionado (`../pos` para
 * el admin, `../home` para el resto): aquí no se compara ningún perfil.
 */
const useOrderPosNavigation = (ordersService: OrdersService, posRoute: string, currentOrder: Order | undefined) => {
    const navigation = useNavigate();

    // Cutover F6: el POS 2.0 es el ÚNICO POS (el v1 se eliminó del código) —
    // para admin vive en `pos`, para manager/colaborador ES el home. Una orden
    // v1 que siga viva (creada antes del corte) ya no tiene POS con qué
    // retomarse: devuelve null y el caller avisa — se resuelve desde la lista
    // (marcar pagada/completar/cancelar), que sigue siendo dual v1/v2.
    const getPosRouteForOrder = (order: Order): string | null => {
        const lines = (order.products || []).filter(Boolean);
        const hasV1OnlyLines = lines.length > 0 && !lines.some((line) => isOrderLineV2(line));
        if (hasV1OnlyLines) {
            return null;
        }
        return posRoute;
    };

    const navigateToPosForOrder = (order: Order, orderId: string | undefined) => {
        const posRouteForOrder = getPosRouteForOrder(order);
        if (!posRouteForOrder) {
            showAlertMessage(
                'Este pedido trae productos del menú anterior y ya no puede editarse en el POS — complétalo, márcalo pagado o cancélalo desde la lista'
            );
            return false;
        }
        navigation(posRouteForOrder, { state: { orderId } });
        return true;
    };

    const onCloseOrdersByTable = async (order: Order | undefined, customerUid?: string) => {
        showLoader();
        try {
            if (order?.tableKiosk?.id) {
                const consolidatedOrder = await ordersService.completeTableOrders(order.tableKiosk.id, customerUid);
                if (navigateToPosForOrder(consolidatedOrder, consolidatedOrder.id)) {
                    showAlertMessage('Órdenes de mesa consolidadas exitosamente');
                }
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // Al cerrar una mesa hay que ver DE QUIÉN es cada consumo: puede haber cuentas
    // de personas distintas (alguien que se fue sin pagar y quien se sentó después).
    // Cobrarlas juntas a ciegas es justo lo que reportó el negocio.
    const onConfirmCloseOrdersByTable = async (order: Order | undefined = currentOrder) => {
        try {
            const tableId = order?.tableKiosk?.id;
            if (!tableId) return;
            const ordersOfTable = await ordersService.getOrdersByTable(tableId);
            const totalDe = (list: Order[]) => list.reduce((sum, o) => sum + (o.total || o.subtotal || 0), 0);
            const porCliente = new Map<string, { nombre: string; orders: Order[] }>();
            ordersOfTable.forEach((order) => {
                const uid = order.customer?.uid || 'sin-cuenta';
                const nombre =
                    `${order.customer?.profile?.name || ''} ${order.customer?.profile?.lastName || ''}`.trim() ||
                    'Sin cuenta';
                if (!porCliente.has(uid)) porCliente.set(uid, { nombre, orders: [] });
                porCliente.get(uid)!.orders.push(order);
            });

            const detalle = Array.from(porCliente.values())
                .map((c) => `• ${c.nombre}: ${c.orders.length} pedido(s) — ${currencyFormatter(totalDe(c.orders))}`)
                .join('\n');

            // Un solo cliente: como siempre, sin fricción
            if (porCliente.size <= 1) {
                showIOSAlert({
                    title: 'Cobrar mesa',
                    message: `Se juntarán los pedidos de la mesa en una sola cuenta para cobrarla:\n\n${detalle}\n\nLa mesa se libera al completar el cobro.`,
                    buttons: [{ text: 'Continuar', onPress: () => onCloseOrdersByTable(order) }]
                });
                return;
            }

            // Varias personas: el staff decide a quién le cobra
            const actualUid = order?.customer?.uid;
            const actual = actualUid ? porCliente.get(actualUid) : undefined;
            showIOSAlert({
                title: '⚠️ La mesa tiene cuentas de varias personas',
                message: `${detalle}\n\nVerifica con el cliente qué consumió antes de cobrar. Puedes cobrar solo su cuenta y dejar el resto abierto.`,
                buttons: [
                    ...(actual
                        ? [
                              {
                                  text: `Solo ${actual.nombre} (${currencyFormatter(totalDe(actual.orders))})`,
                                  onPress: () => onCloseOrdersByTable(order, actualUid)
                              }
                          ]
                        : []),
                    {
                        text: `Toda la mesa (${currencyFormatter(totalDe(ordersOfTable))})`,
                        onPress: () => onCloseOrdersByTable(order)
                    }
                ]
            });
        } catch (error: any) {
            showAlertError(error);
        }
    };

    const onModifyOrder = () => {
        if (!currentOrder) return;
        navigateToPosForOrder(currentOrder, currentOrder.id);
    };

    return { navigateToPosForOrder, onConfirmCloseOrdersByTable, onModifyOrder };
};

export default useOrderPosNavigation;
